import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppUser } from '../../../hooks/useAppUser';
import { useApi } from '../../../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloudIcon from '@mui/icons-material/Cloud';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import MuiTooltip from '@mui/material/Tooltip';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

function StatCard({ title, value, subtitle, icon, color = 'primary.main', loading = false, trend, onClick }) {
  return (
    <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { bgcolor: 'action.hover' } : {} }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" gutterBottom sx={{
              color: "text.secondary"
            }}>
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="h4" component="div">
                {value}
              </Typography>
            )}
            {subtitle && !loading && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  display: 'block'
                }}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && !loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 14, color: trend >= 0 ? 'success.main' : 'error.main', mr: 0.5 }} />
                <Typography variant="caption" color={trend >= 0 ? 'success.main' : 'error.main'}>
                  {trend >= 0 ? '+' : ''}{trend}% vs last month
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.7 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function UsageTrendsChart({ data, loading }) {
  const [hiddenLines, setHiddenLines] = React.useState({});

  const handleLegendClick = (dataKey) => {
    setHiddenLines((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{
          color: "text.secondary"
        }}>
          No usage data available yet
        </Typography>
      </Box>
    );
  }

  const allLines = [
    { dataKey: 'participantMins', name: 'Participant Mins', stroke: '#8884d8', yAxisId: 'left' },
    { dataKey: 'rooms', name: 'Rooms', stroke: '#82ca9d', yAxisId: 'right' },
    { dataKey: 'recordingMins', name: 'Recording Mins', stroke: '#ffc658', yAxisId: 'right' },
    { dataKey: 'streamingMins', name: 'Streaming Mins', stroke: '#00bcd4', yAxisId: 'right' },
    { dataKey: 'transcriptionMins', name: 'Transcription Mins', stroke: '#e91e63', yAxisId: 'right' },
    { dataKey: 'compositionMins', name: 'Composition Mins', stroke: '#ff7300', yAxisId: 'right' },
  ];

  // Only show lines that have data (aggregate > 0)
  const lines = allLines.filter((line) => {
    const total = data.reduce((sum, item) => sum + (item[line.dataKey] || 0), 0);
    return total > 0;
  });

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 5, right: 50, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis
          dataKey="date"
          tickFormatter={(value) => dayjs(value).format('MMM D')}
          stroke="rgba(255,255,255,0.5)"
          fontSize={11}
        />
        <YAxis
          yAxisId="left"
          stroke="#8884d8"
          fontSize={11}
          tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="#82ca9d"
          fontSize={11}
        />
        <Tooltip
          labelFormatter={(value) => dayjs(value).format('MMM D, YYYY')}
          formatter={(value, name) => [value.toLocaleString(), name]}
          contentStyle={{
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
          }}
        />
        <Legend
          onClick={(e) => handleLegendClick(e.dataKey)}
          wrapperStyle={{ cursor: 'pointer' }}
          formatter={(value, entry) => (
            <span style={{ color: hiddenLines[entry.dataKey] ? '#666' : entry.color, textDecoration: hiddenLines[entry.dataKey] ? 'line-through' : 'none' }}>
              {value}
            </span>
          )}
        />
        {lines.map((line) => (
          <Line
            key={line.dataKey}
            yAxisId={line.yAxisId}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.stroke}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
            hide={hiddenLines[line.dataKey]}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function CostBreakdownChart({ data, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{
          color: "text.secondary"
        }}>
          No cost data yet
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis type="number" stroke="rgba(255,255,255,0.5)" fontSize={12} tickFormatter={(v) => `$${v}`} />
        <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.5)" fontSize={12} />
        <Tooltip
          formatter={(value) => [`$${value.toFixed(2)}`, 'Cost']}
          contentStyle={{
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
          }}
        />
        <Bar dataKey="cost" fill="#8884d8" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function RecentActivityList({ activity, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!activity || activity.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{
          color: "text.secondary"
        }}>
          No recent activity
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ overflow: 'auto', maxHeight: 'calc(100% - 8px)', p: 0 }}>
      {activity.map((room) => (
        <ListItem
          key={room.id}
          component={Link}
          to={`/rooms/${room.id}`}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            py: 1,
            px: 1.5,
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <FiberManualRecordIcon
              sx={{
                fontSize: 10,
                color: room.active ? 'success.main' : 'text.disabled',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}>
                  {room.name || 'Unnamed Room'}
                </Typography>
                {room.active && (
                  <Chip label="Live" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }} />
                )}
                {(room.recorded || room.recordingActive) && (
                  <MuiTooltip title={room.recordingActive ? 'Recording in progress' : 'Recorded'}>
                    <VideoCameraFrontIcon
                      sx={{
                        fontSize: 14,
                        flexShrink: 0,
                        color: room.recordingActive ? 'error.main' : 'text.secondary',
                      }}
                    />
                  </MuiTooltip>
                )}
                {(room.streamed || room.streamingActive) && (
                  <MuiTooltip title={room.streamingActive ? 'Streaming live' : 'Streamed'}>
                    <LiveTvIcon
                      sx={{
                        fontSize: 14,
                        flexShrink: 0,
                        color: room.streamingActive ? 'error.main' : 'text.secondary',
                      }}
                    />
                  </MuiTooltip>
                )}
              </Box>
            }
            secondary={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: "text.secondary",
                      flex: 1,
                      minWidth: 0
                    }}>
                    {room.owner || 'Unknown'}
                  </Typography>
                  {room.region && (
                    <>
                      <PublicIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          flexShrink: 0
                        }}>
                        {room.region}
                      </Typography>
                    </>
                  )}
                </Box>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {room.participants} participant{room.participants !== 1 ? 's' : ''} • {dayjs(room.timeStarted).fromNow()}
                  {room.duration && ` • ${room.duration}m`}
                </Typography>
              </Box>
            }
            slotProps={{
              primary: { component: 'div' },
              secondary: { component: 'div' }
            }} />
        </ListItem>
      ))}
    </List>
  );
}

/**
 * Merge room trends and credit trends into a unified dataset for the chart
 */
function mergeTrendData(roomTrends, creditTrends) {
  const dataMap = new Map();

  // Add room data
  (roomTrends || []).forEach((item) => {
    dataMap.set(item._id, {
      date: item._id,
      rooms: item.count || 0,
      participants: item.participants || 0,
      participantMins: 0,
      recordingMins: 0,
      streamingMins: 0,
      transcriptionMins: 0,
      compositionMins: 0,
      totalCost: 0,
    });
  });

  // Merge credit data
  (creditTrends || []).forEach((item) => {
    if (dataMap.has(item._id)) {
      const existing = dataMap.get(item._id);
      existing.participantMins = Math.round(item.participantMins || 0);
      existing.recordingMins = Math.round(item.recordingMins || 0);
      existing.streamingMins = Math.round(item.streamingMins || 0);
      existing.transcriptionMins = Math.round(item.transcriptionMins || 0);
      existing.compositionMins = Math.round(item.compositionMins || 0);
      existing.totalCost = item.totalCost || 0;
    } else {
      dataMap.set(item._id, {
        date: item._id,
        rooms: 0,
        participants: 0,
        participantMins: Math.round(item.participantMins || 0),
        recordingMins: Math.round(item.recordingMins || 0),
        streamingMins: Math.round(item.streamingMins || 0),
        transcriptionMins: Math.round(item.transcriptionMins || 0),
        compositionMins: Math.round(item.compositionMins || 0),
        totalCost: item.totalCost || 0,
      });
    }
  });

  // Sort by date and return array
  return Array.from(dataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function Dashboard() {
  const { user } = useAppUser();
  const api = useApi();
  const navigate = useNavigate();

  // Include user ID in query keys so cache is per-user
  const userId = user?.id || user?._id || user?.email;

  // Fetch onboarding status from the API
  const { data: tenantStatus, isLoading: tenantStatusLoading } = useQuery({
    queryKey: ['onboarding-status', userId],
    queryFn: () => api.get('/api/organizations/onboarding-status'),
    retry: 1,
    enabled: !!userId,
  });

  // Redirect to verification pending if email not verified
  useEffect(() => {
    if (user && user.emailVerified !== true) {
      navigate('/verification-pending', { replace: true });
    }
  }, [user, navigate]);

  // Redirect to onboarding if not completed
  useEffect(() => {
    if (tenantStatus && !tenantStatus.onboardingCompleted) {
      navigate('/organization/setup', { replace: true });
    }
  }, [tenantStatus, navigate]);

  // User has tenant if they have an apiKey
  const hasTenant = tenantStatus?.hasApiKey;

  // Fetch usage summary for stat cards (only if user has tenant)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', userId],
    queryFn: () => api.get('/api/usage/summary'),
    refetchInterval: 30000,
    enabled: !!hasTenant && !!userId,
  });

  // Fetch usage trends for chart (only if user has tenant)
  const { data: trends, isLoading: trendsLoading } = useQuery({
    queryKey: ['dashboard-trends', userId],
    queryFn: () => api.get('/api/usage/trends/daily'),
    refetchInterval: 60000,
    enabled: !!hasTenant && !!userId,
  });

  // Fetch recent room activity (only if user has tenant)
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity', userId],
    queryFn: () => api.get('/api/usage/rooms/activity', { limit: 10 }),
    refetchInterval: 30000,
    enabled: !!hasTenant && !!userId,
  });

  // Fetch cost breakdown (only if user has tenant)
  const { data: breakdown, isLoading: breakdownLoading } = useQuery({
    queryKey: ['dashboard-breakdown', userId],
    queryFn: () => api.get('/api/usage/costs/breakdown'),
    refetchInterval: 60000,
    enabled: !!hasTenant && !!userId,
  });

  // Merge room and credit trends for the chart
  const chartData = trends ? mergeTrendData(trends.rooms, trends.credits) : [];

  // Transform breakdown data for bar chart
  const costBreakdownData = breakdown?.breakdown ? [
    { name: 'Participants', cost: parseFloat(breakdown.breakdown.participants?.cost) || 0 },
    { name: 'Recording', cost: parseFloat(breakdown.breakdown.recording?.cost) || 0 },
    { name: 'Streaming', cost: parseFloat(breakdown.breakdown.streaming?.cost) || 0 },
    { name: 'Transcription', cost: parseFloat(breakdown.breakdown.transcription?.cost) || 0 },
    { name: 'Composition', cost: parseFloat(breakdown.breakdown.composition?.cost) || 0 },
    { name: 'Storage', cost: parseFloat(breakdown.breakdown.storage?.cost) || 0 },
  ].filter(item => item.cost > 0) : [];

  // Show loading state while checking tenant status
  if (tenantStatusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show tenant setup prompt if user doesn't have one yet
  if (!hasTenant) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Welcome to Hiyve SDK Admin
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4
          }}>
          Hello, {user?.name || user?.email}
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 4 }}>
          <MeetingRoomIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Set Up Your Tenant
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              mb: 3
            }}>
            To view your dashboard and usage statistics, you need to set up your tenant first.
            This will generate your API keys and enable SDK access.
          </Typography>
          <Box
            component="a"
            href="/organization/setup"
            sx={{
              display: 'inline-block',
              px: 4,
              py: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              textDecoration: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Get Started
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" sx={{
            color: "text.secondary"
          }}>
            Welcome back, {user?.name || user?.email}
            {stats?.period && (
              <> — Showing data for {dayjs(stats.period.start).format('MMM D')} - {dayjs(stats.period.end).format('MMM D, YYYY')}</>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VpnKeyIcon />}
            component={Link}
            to="/api-keys"
          >
            API Keys
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BarChartIcon />}
            component={Link}
            to="/usage"
          >
            Detailed Usage
          </Button>
        </Box>
      </Box>
      {/* Live Status Banner */}
      {(stats?.active?.rooms > 0 || stats?.active?.recordings > 0 || stats?.active?.streamings > 0 || stats?.active?.transcriptions > 0 || stats?.active?.compositions > 0) && (
        <Paper
          sx={{ p: 2, mb: 3, bgcolor: 'success.dark', display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', '&:hover': { opacity: 0.9 } }}
          onClick={() => navigate('/rooms/live')}
        >
          <FiberManualRecordIcon sx={{ color: 'success.light', animation: 'pulse 2s infinite' }} />
          <Typography variant="body1">
            <strong>{stats.active.rooms || 0}</strong> active room{stats.active.rooms !== 1 ? 's' : ''}
            {stats.active.participants > 0 && (
              <> with <strong>{stats.active.participants}</strong> participant{stats.active.participants !== 1 ? 's' : ''}</>
            )}
            {stats.active.recordings > 0 && (
              <>, <strong>{stats.active.recordings}</strong> recording{stats.active.recordings !== 1 ? 's' : ''}</>
            )}
            {stats.active.streamings > 0 && (
              <>, <strong>{stats.active.streamings}</strong> live stream{stats.active.streamings !== 1 ? 's' : ''}</>
            )}
            {stats.active.transcriptions > 0 && (
              <>, <strong>{stats.active.transcriptions}</strong> transcription{stats.active.transcriptions !== 1 ? 's' : ''}</>
            )}
            {stats.active.compositions > 0 && (
              <>, <strong>{stats.active.compositions}</strong> composition{stats.active.compositions !== 1 ? 's' : ''}</>
            )}
          </Typography>
        </Paper>
      )}
      {/* Free Minutes Card */}
      {stats?.freeMinutes && (
        <Paper sx={{ p: 2, mb: 3, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/usage')}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{
              fontWeight: 500
            }}>
              Free Minutes This Month
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              Resets {dayjs(stats.freeMinutes.nextResetDate).format('MMM D')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="h4" color={stats.freeMinutes.percentUsed >= 100 ? 'error.main' : 'primary.main'}>
              {stats.freeMinutes.remaining.toLocaleString()}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                ml: 1
              }}>
              / {stats.freeMinutes.totalAllocation.toLocaleString()} remaining
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={Math.min(stats.freeMinutes.percentUsed, 100)}
            color={stats.freeMinutes.percentUsed >= 100 ? 'error' :
                   stats.freeMinutes.percentUsed >= 80 ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 1 }}
          />

          {stats.freeMinutes.percentUsed >= 100 && (
            <Typography
              variant="caption"
              sx={{
                color: "error.main",
                mt: 1,
                display: 'block'
              }}>
              Free allocation exceeded - charges apply ({stats.freeMinutes.exceededBy.toLocaleString()} mins over)
            </Typography>
          )}
          {stats.freeMinutes.percentUsed >= 80 && stats.freeMinutes.percentUsed < 100 && (
            <Typography
              variant="caption"
              sx={{
                color: "warning.main",
                mt: 1,
                display: 'block'
              }}>
              {100 - stats.freeMinutes.percentUsed}% of free minutes remaining
            </Typography>
          )}
        </Paper>
      )}
      {/* Primary Stat Cards */}
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          mb: 1,
          display: 'block'
        }}>
        This Month
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Total Rooms"
            value={(stats?.month?.rooms ?? 0).toLocaleString()}
            subtitle={`${stats?.active?.rooms || 0} active now`}
            icon={<MeetingRoomIcon sx={{ fontSize: 40 }} />}
            color="primary.main"
            loading={statsLoading}
            onClick={() => navigate('/rooms')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Participant Minutes"
            value={(stats?.credits?.participantMinutes ?? 0).toLocaleString()}
            subtitle={`${stats?.active?.participants || 0} active now • $${(parseFloat(breakdown?.breakdown?.participants?.cost) || 0).toFixed(2)} cost`}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="success.main"
            loading={statsLoading}
            onClick={() => navigate('/usage')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Recordings"
            value={(stats?.month?.recordings ?? 0).toLocaleString()}
            subtitle={`${stats?.active?.recordings || 0} active now • ${stats?.credits?.recordingMinutes || 0} minutes`}
            icon={<VideoCameraFrontIcon sx={{ fontSize: 40 }} />}
            color="secondary.main"
            loading={statsLoading}
            onClick={() => navigate('/recordings')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <StatCard
            title="Total Cost"
            value={`$${stats?.credits?.totalCost ?? '0.00'}`}
            subtitle="This billing period"
            icon={<AttachMoneyIcon sx={{ fontSize: 40 }} />}
            color="warning.main"
            loading={statsLoading}
            onClick={() => navigate('/billing')}
          />
        </Grid>
      </Grid>
      {/* Secondary Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { icon: <LiveTvIcon color="info" />, value: (stats?.credits?.streamingMinutes ?? 0).toLocaleString(), label: 'Streaming Mins', path: '/recordings' },
          { icon: <SubtitlesIcon color="secondary" />, value: (stats?.credits?.transcriptionMinutes ?? 0).toLocaleString(), label: 'Transcription Mins', path: '/recordings' },
          { icon: <VideoCameraFrontIcon color="warning" />, value: (stats?.credits?.compositionMinutes ?? 0).toLocaleString(), label: 'Composition Mins', path: '/recordings' },
          { icon: <MeetingRoomIcon color="primary" />, value: stats?.month?.streamings ?? 0, label: 'Live Streams', path: '/recordings' },
          { icon: <CloudIcon color="info" />, value: `${stats?.storage?.usedGB?.toFixed(2) ?? '0.00'} GB`, label: 'Storage Used', path: '/usage' },
        ].map((item) => (
          <Grid
            key={item.label}
            size={{
              xs: 6,
              sm: 2.4
            }}>
            <Card sx={{ py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(item.path)}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.icon}
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Usage Trends Chart */}
        <Grid
          size={{
            xs: 12,
            lg: 8
          }}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" gutterBottom sx={{
              fontWeight: 500
            }}>
              Usage Trends (30 Days)
            </Typography>
            <Box sx={{ height: 'calc(100% - 32px)' }}>
              <UsageTrendsChart data={chartData} loading={trendsLoading} />
            </Box>
          </Paper>
        </Grid>

        {/* Cost Breakdown */}
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 4
          }}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" gutterBottom sx={{
              fontWeight: 500
            }}>
              Cost Breakdown
            </Typography>
            <Box sx={{ height: 'calc(100% - 32px)' }}>
              {costBreakdownData.length > 0 ? (
                <CostBreakdownChart data={costBreakdownData} loading={breakdownLoading} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography sx={{
                    color: "text.secondary"
                  }}>
                    No costs recorded yet
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 4
          }}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{
                fontWeight: 500
              }}>
                Recent Activity
              </Typography>
              <Button size="small" component={Link} to="/rooms">
                View All
              </Button>
            </Box>
            <Box sx={{ height: 'calc(100% - 36px)' }}>
              <RecentActivityList
                activity={activityData?.activity}
                loading={activityLoading}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Cost Summary Table */}
        <Grid
          size={{
            xs: 12,
            lg: 8
          }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Cost Summary
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Participant Minutes', value: breakdown?.breakdown?.participants?.minutes || 0, cost: breakdown?.breakdown?.participants?.cost || '0.00', color: 'primary', path: '/usage' },
                { label: 'Recording Minutes', value: breakdown?.breakdown?.recording?.minutes || 0, cost: breakdown?.breakdown?.recording?.cost || '0.00', color: 'secondary', path: '/recordings' },
                { label: 'Streaming Minutes', value: breakdown?.breakdown?.streaming?.minutes || 0, cost: breakdown?.breakdown?.streaming?.cost || '0.00', color: 'info', path: '/recordings' },
                { label: 'Transcription Minutes', value: breakdown?.breakdown?.transcription?.minutes || 0, cost: breakdown?.breakdown?.transcription?.cost || '0.00', color: 'warning', path: '/recordings' },
                { label: 'Composition Minutes', value: breakdown?.breakdown?.composition?.minutes || 0, cost: breakdown?.breakdown?.composition?.cost || '0.00', color: 'success', path: '/recordings' },
                { label: 'Storage (GB)', value: parseFloat(breakdown?.breakdown?.storage?.usedGB || 0).toFixed(2), cost: breakdown?.breakdown?.storage?.cost || '0.00', color: 'primary', path: '/usage' },
              ].map((item) => (
                <Grid
                  key={item.label}
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 2
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(item.path)}>
                    <Typography variant="h5" color={`${item.color}.main`}>
                      {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block"
                      }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{
                      fontWeight: "medium"
                    }}>
                      ${item.cost}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{
                color: "text.secondary"
              }}>
                Total Cost This Period
              </Typography>
              <Typography variant="h5" sx={{
                color: "error.main"
              }}>
                ${breakdown?.breakdown?.total || '0.00'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </Box>
  );
}

export default Dashboard;
